/**
 * One shape for "I am fetching something", used by every screen.
 *
 * The screens used to each hold `const [rows, setRows] = useState<T[] | null>()`
 * plus `const [error, setError] = useState('')`, and then render
 * `if (error) … if (!rows) … if (!rows.length) …`. That works until the day a
 * call fails AFTER a successful one, at which point `rows` is still populated
 * and the operator is looking at stale data with a red line above it and no way
 * to tell which is true. A single discriminated union cannot be in two states at
 * once, so that class of lie is not expressible here.
 *
 * `cause` is kept alongside the message on purpose: `Result` in ui.tsx needs the
 * original ApiError to tell a route that is not deployed yet (render a stub)
 * from a route that is deployed and broke (render an alarm). A flattened string
 * throws that away.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type AsyncState<T> =
  | { state: 'loading' }
  | { state: 'ok'; value: T }
  | { state: 'error'; error: string; cause: unknown };

export function message(err: unknown): string {
  if (err instanceof Error) return err.message || err.name;
  return String(err);
}

/**
 * Run `load` whenever `deps` change, and give back a reload handle.
 *
 * The sequence number is not decoration. Filters here are typed into inputs and
 * re-fire the fetch on every change; without it a slow response for "fam-1"
 * lands after a fast one for "fam-2" and the table shows the wrong family's data
 * under the right family's filter. On this surface that is somebody's credits.
 */
export function useAsync<T>(
  load: () => Promise<T>,
  deps: ReadonlyArray<unknown>,
): { result: AsyncState<T>; reload: () => void } {
  const [result, setResult] = useState<AsyncState<T>>({ state: 'loading' });
  const [nonce, setNonce] = useState(0);
  const seq = useRef(0);

  // `load` is a new closure on every render, so it cannot be a dependency
  // without re-fetching forever. The caller's `deps` are the truth about when
  // the work changes; the ref just makes sure the effect calls the newest
  // closure rather than the one captured on mount.
  const latest = useRef(load);
  latest.current = load;

  useEffect(() => {
    const mine = ++seq.current;
    setResult({ state: 'loading' });
    latest.current().then(
      (value) => {
        if (mine === seq.current) setResult({ state: 'ok', value });
      },
      (err: unknown) => {
        if (mine === seq.current) {
          setResult({ state: 'error', error: message(err), cause: err });
        }
      },
    );
  }, [...deps, nonce]);

  return { result, reload: useCallback(() => setNonce((n) => n + 1), []) };
}

/** The value if it arrived, otherwise null. For a card that degrades rather
 *  than blanking the page it sits on. */
export function valueOf<T>(state: AsyncState<T>): T | null {
  return state.state === 'ok' ? state.value : null;
}

/* --- several sources at once ----------------------------------------------
   The dashboard reads five endpoints, two of which are new and may not be
   deployed yet. `Promise.all` would blank the entire first screen an operator
   sees because one card's endpoint 404s, which is precisely the moment a
   console has to keep working. `settled` keeps each tile's success or failure
   to itself. */

export type Settled<T> = { ok: true; value: T } | { ok: false; error: string; cause: unknown };

export async function settled<T>(work: Promise<T>): Promise<Settled<T>> {
  try {
    return { ok: true, value: await work };
  } catch (err) {
    return { ok: false, error: message(err), cause: err };
  }
}
